import React, { useEffect, useRef, useState, useMemo } from "react";
import TopSalesCard from "../components/dashboard/TopSalesCard";
import RecentSales from "../components/dashboard/RecentSales";
import WeeklySalesReport from "../components/dashboard/WeeklySalesReport";
import InventoryCard from "../components/dashboard/InventoryCard";
import AiPromptsBtn from "../components/dashboard/AiPromptsBtn";

import KpiCard from "../components/dashboard/KpiCard";
import { Container, Row, Col } from "react-bootstrap";
import Header from "../components/globals/headers/header";
import SubHeader from "../components/globals/headers/subHeader";
import { getApiClient } from "../libs/axiosInstance";
import { loadAuth } from "../libs/authMgmt";
import { Link } from "react-router";
import HourlySalesChart from "../components/dashboard/HourlySales";
import LeaderboardCard from "../components/dashboard/LeaderboardCard";
import Wrapper from "./_Wrapper";
import AISalesCard from "../components/dashboard/AISalesCard";
import AiSalesForecast from "../components/dashboard/AiProjectedSales";
import BusinessHealthSection from "../components/dashboard/businessHealth";

// --- Local helpers (pure functions) ---
const fmtMoney = (n, cc) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: cc || "PHP",
  }).format(Number.isFinite(n) ? n : 0);

const ymd = (d) => {
  const dt = new Date(d);
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

// Simple inline bar chart row (no extra deps)
function BarRow({ label, value = 0, max = 1 }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div style={{ marginBottom: 8 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: 12,
          marginBottom: 4,
        }}
      >
        <span style={{ opacity: 0.8 }}>{label}</span>
        <span style={{ fontVariantNumeric: "tabular-nums" }}>{value}</span>
      </div>
      <div
        style={{
          width: "100%",
          height: 8,
          background: "#eee",
          borderRadius: 6,
          overflow: "hidden",
        }}
      >
        <div
          style={{ width: `${pct}%`, height: "100%", background: "#4CAF50" }}
        />
      </div>
    </div>
  );
}

export default function MainPage() {
  document.title = `Dashboard - ${loadAuth("company")?.name ?? "Dashboard"}`;

  const [totalSales, setTotalSales] = useState(0);
  const [totalExpenses, setTotalExpenses] = useState(0);
  const [otherExpenses, setOtherExpenses] = useState(0);
  const [payrollExpenses, setPayrollExpenses] = useState(0);
  const [alreadyOnboarded, setAlreadyOnboarded] = useState(true);
  const [topSalesData, setTopSalesData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Hourly/day filter
  const [selectedDay, setSelectedDay] = useState(() => ymd(new Date()));
  const [hourlySales, setHourlySales] = useState([]); // [{hour:0-23, amount:number}]
  const [hourlyInfo, setHourlyInfo] = useState({
    insufficient: false,
    insufficientReason: null,
    busiestHour: null,
  });
  const [hourlyLoading, setHourlyLoading] = useState(true);

  const [selectedWeekday, setSelectedWeekday] = useState(
    (() => {
      // ISO weekday for today: 1..7
      const js = new Date().getDay(); // 0..6 (Sun..Sat)
      return js === 0 ? 7 : js;
    })()
  );

  const [kpiRange, setKpiRange] = useState("monthly"); // "monthly" | "yearly" | "all"
  const [kpiMonth, setKpiMonth] = useState(() => new Date().getMonth() + 1); // 1..12 (used only if monthly)
  const [kpiMonthHourly, setKpiMonthHourly] = useState(
    () => new Date().getMonth() + 1
  ); // 1..12 (used only if monthly)
  const [kpiYear, setKpiYear] = useState(() => new Date().getFullYear());

  const [topProductRec, setTopProductRec] = useState("");

  const apiBase = process.env.REACT_APP_API_URL;
  const subdomain = loadAuth("company")?.subdomain ?? "XXX";
  const currency = loadAuth("company")?.currency ?? "PHP";

  const axiosRef = useRef(null);
  if (!axiosRef.current) {
    axiosRef.current = getApiClient();
  }

  const axios = axiosRef.current;
  const hourlyTimer = useRef(null);
  const hourlyInFlight = useRef(false);

  useEffect(() => {
    clearTimeout(hourlyTimer.current);
    hourlyTimer.current = setTimeout(async () => {
      if (hourlyInFlight.current) return;
      hourlyInFlight.current = true;
      setHourlyLoading(true);

      const axios = axiosRef.current;
      try {
        const resp = await axios.get(
          `/api/stats/hourly?weekday=${selectedWeekday}`
        );
        const payload = resp.data || {};
        const hours = Array.isArray(payload.hours) ? payload.hours : [];
        // map to chart shape: use avgAmount as the plotted value
        const map = new Map(
          hours.map((r) => [Number(r.hour), Number(r.avgAmount) || 0])
        );
        const normalized = Array.from({ length: 24 }, (_, h) => ({
          hour: h,
          amount: map.get(h) ?? 0,
        }));

        setHourlySales(normalized);
        setHourlyInfo({
          insufficient: !!payload.insufficient,
          insufficientReason: payload.insufficientReason || null,
          busiestHour: payload.busiestHour || null,
        });
      } catch (e) {
        setHourlySales([]);
        setHourlyInfo({
          insufficient: true,
          insufficientReason: "Unable to fetch hourly averages.",
          busiestHour: null,
        });
      } finally {
        setHourlyLoading(false);
        hourlyInFlight.current = false;
      }
    }, 250);

    return () => clearTimeout(hourlyTimer.current);
  }, [selectedWeekday]);

  useEffect(() => {
    let cancelled = false;
    setTopProductRec("");
    (async () => {
      const axios = axiosRef.current;

      // Build query for KPI API
      const qs = new URLSearchParams({
        range: kpiRange,
        ...(kpiRange === "monthly"
          ? { month: String(kpiMonth), year: String(kpiYear) }
          : {}),
        ...(kpiRange === "yearly" ? { year: String(kpiYear) } : {}),
      }).toString();

      setTopSalesData(null);
      setIsLoading(true);
      try {
        // Sales total by range
        const kpiResp = await axios.get(`/api/stats/kpis?${qs}`);
        const { totalSales = 0 } = kpiResp.data || { totalSales: 0 };
        setTotalSales(totalSales);

        // --- Stock value (unitCost * totalStock) and Other Expenses
        // const resItems = await axios.get("/api/items");
        // const stockValue = (resItems.data || []).reduce((sum, row) => {
        //   const cost = (parseFloat(row.unitCost) || 0) * (row.totalStock || 0);
        //   return sum + cost;
        // }, 0);
        const stockResp = await axios.get(`/api/stats/stock-expense?${qs}`);
        const { totals } = stockResp.data || {};
        const stockValue = totals.totalExpense ?? 0;

        // Expenses (optional: if you add similar filters to your existing endpoints)
        const expResp = await axios.get(`/api/stats/expenses?${qs}`);
        const { totalOtherExpenses = 0 } = expResp.data || {
          totalOtherExpenses: 0,
        };
        setOtherExpenses(totalOtherExpenses);

        const payResp = await axios.get(`/api/stats/payroll?${qs}`);
        const { totalPayroll = 0 } = payResp.data || { totalPayroll: 0 };
        setPayrollExpenses(totalPayroll);

        setTotalExpenses(stockValue + totalOtherExpenses + totalPayroll);

        //set top products
        const topResp = await axios.get(`/api/stats/top-products?ai=1&${qs}`);
        const { rows = [], recommendationsText } = topResp.data || {};
        setTopProductRec(recommendationsText);
        setTopSalesData(rows);

        // If you keep existing expense logic for now, leave it as-is
        // and just recompute totalExpenses as you already do.
      } catch (e) {
        console.warn("KPI fetch failed", e);
        if (!cancelled) setTotalSales(0);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [kpiRange, kpiMonth, kpiYear]);

  // useEffect(() => {
  //   const fetchKpisAndExpenses = async () => {
  //     try {
  //       // --- Sales total (existing)
  //       const response = await axios.get("/api/sales/top");
  //       const total = (response.data || []).reduce((sum, item) => sum + (item.totalRevenue || 0), 0);
  //       setTotalSales(total);

  //       // --- Stock value (unitCost * totalStock) and Other Expenses
  //       const resItems = await axios.get("/api/items");
  //       const stockValue = (resItems.data || []).reduce((sum, row) => {
  //         const cost = (parseFloat(row.unitCost) || 0) * (row.totalStock || 0);
  //         return sum + cost;
  //       }, 0);

  //       const resOther = await axios.get("/api/expenses");
  //       const _other = (resOther.data || []).reduce((sum, row) => sum + (row.amount || 0), 0);
  //       setOtherExpenses(_other);

  //       // --- Payroll
  //       const resPayroll = await axios.get("/api/payroll");
  //       const _payroll = (resPayroll.data || []).reduce((sum, row) => sum + (parseFloat(row.totalPayrollCost) || 0), 0);
  //       setPayrollExpenses(_payroll);

  //       setTotalExpenses(stockValue + _other + _payroll);
  //     } catch (err) {
  //       console.error("Failed to fetch KPI/expense data", err);
  //     } finally {
  //       setIsLoading(false);
  //     }
  //   };

  //   const checkOnboarded = async () => {
  //     try {
  //       const resp = await axios.get(`${apiBase}/api/companies/${subdomain}/stripe/connection-status`);
  //       setAlreadyOnboarded(!!resp?.data?.isReady);
  //     } catch (e) {
  //       console.error("Stripe connection status error:", e);
  //     }
  //   };

  //   fetchKpisAndExpenses();
  //   checkOnboarded();
  // }, [apiBase, axios, subdomain]);

  // Header sizing logic (kept from your file)
  const [headerWidth, setHeaderWidth] = useState(0);
  const elementRef = useRef(null);
  useEffect(() => {
    if (elementRef.current) {
      setHeaderWidth(elementRef.current.clientWidth - 10);
    }
  }, []);

  // Deriveds
  const stockExpense = Math.max(
    0,
    totalExpenses - otherExpenses - payrollExpenses
  );
  const profit = (totalSales || 0) - (totalExpenses || 0);

  return (
    <>
      <Wrapper>
        <Container fluid>
          <Row>
            <Col>
              <div className="dashboard_cont">
                <div className="dashboard_header">
                  <h1>Dashboard</h1>
                </div>

                {alreadyOnboarded === false &&
                !localStorage.getItem("closed_stripe_onboard_alert") ? (
                  <div
                    className="alert alert-warning alert-dismissible fade show"
                    role="alert"
                  >
                    To enable card payments and have funds deposited directly
                    into your linked bank account, please complete the{" "}
                    <b>Stripe onboarding</b> process by{" "}
                    <Link to="/settings">clicking here</Link>.
                    <button
                      type="button"
                      className="btn-close close"
                      data-bs-dismiss="alert"
                      aria-label="Close"
                      onClick={() =>
                        localStorage.setItem("closed_stripe_onboard_alert", "1")
                      }
                    />
                  </div>
                ) : null}

                {/* KPI Cards */}
                <div className="dashboard_card_stats">
                  <Container fluid className="np">
                    <Row>
                      <Col className="mb-3" lg="4">
                        <div
                          className="d-flex align-items-center"
                          style={{ gap: 8 }}
                        >
                          <select
                            className="form-select form-select-sm"
                            value={kpiRange}
                            onChange={(e) => setKpiRange(e.target.value)}
                            style={{ minWidth: 130 }}
                            aria-label="Select KPI range"
                          >
                            <option value="monthly">Monthly</option>
                            <option value="yearly">Yearly</option>
                            <option value="all">All time</option>
                          </select>

                          {kpiRange === "monthly" && (
                            <>
                              <select
                                className="form-select form-select-sm"
                                value={kpiMonth}
                                onChange={(e) =>
                                  setKpiMonth(parseInt(e.target.value, 10))
                                }
                                style={{ minWidth: 120 }}
                                aria-label="Select month"
                              >
                                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(
                                  (m) => (
                                    <option key={m} value={m}>
                                      {new Date(2000, m - 1, 1).toLocaleString(
                                        undefined,
                                        {
                                          month: "long",
                                        }
                                      )}
                                    </option>
                                  )
                                )}
                              </select>
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={kpiYear}
                                onChange={(e) =>
                                  setKpiYear(parseInt(e.target.value, 10))
                                }
                                style={{ width: 88 }}
                                aria-label="Year"
                              />
                            </>
                          )}

                          {kpiRange === "yearly" && (
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              value={kpiYear}
                              onChange={(e) =>
                                setKpiYear(parseInt(e.target.value, 10))
                              }
                              style={{ width: 88 }}
                              aria-label="Year"
                            />
                          )}
                        </div>
                      </Col>
                      <Col lg="8"></Col>
                      <Col xl={4} md={6} sm={12} xs={12}>
                        <KpiCard
                          className=""
                          label="Total Sales"
                          width="100%"
                          loadingState={isLoading}
                          value={fmtMoney(totalSales, currency)}
                        />
                      </Col>
                      <Col xl={4} md={6} sm={12} xs={12}>
                        <KpiCard
                          className=""
                          label="Total Expenses"
                          width="100%"
                          loadingState={isLoading}
                          tooltip={`Stock Expense: ${fmtMoney(
                            stockExpense,
                            currency
                          )} · Payroll: ${fmtMoney(
                            payrollExpenses,
                            currency
                          )} · Other: ${fmtMoney(otherExpenses, currency)}`}
                          value={fmtMoney(totalExpenses, currency)}
                        />
                      </Col>
                      <Col xl={4} md={6} sm={12} xs={12}>
                        <KpiCard
                          className=""
                          label="Total Profit"
                          width="100%"
                          loadingState={isLoading}
                          color={profit < 0 ? "#FF5555" : "rgb(24, 244, 24)"}
                          value={fmtMoney(profit, currency)}
                        />
                      </Col>
                    </Row>
                  </Container>
                </div>

                {/* dito ilagay ang bago mong stuff */}
                <div className="mt-4">
                  <BusinessHealthSection />
                  <AiSalesForecast />
                </div>

                {/* Upper grid: Hourly + Top Sales */}
                <div className="stats_upper_grid">
                  {/* <HourlySalesChart data={hourlySales} isLoading={hourlyLoading} info={hourlyInfo} onWeekdayChange={(w) => setSelectedWeekday(w)} weekday={selectedWeekday} /> */}

                  <TopSalesCard recs={topProductRec} data={topSalesData} />
                  {/* <AISalesCard recs={topProductRec} data={topSalesData} /> */}
                </div>

                <div>
                  <HourlySalesChart
                    selfFetch
                    weekday={selectedWeekday}
                    onWeekdayChange={(w) => setSelectedWeekday(w)}
                    month={kpiMonthHourly}
                    onMonthChange={(m) => setKpiMonthHourly(m)}
                  />
                </div>

                {/* Middle: Suggestions + Weekly Sales */}
                <div className="stats_middle_grid">
                  <WeeklySalesReport />
                  <LeaderboardCard />
                </div> 

                {/* Lower: Recent Sales + Inventory */}
                <div className="stats_lower_grid">
                  <RecentSales width="100%" />
                  <InventoryCard width="100%" />
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </Wrapper>
    </>
  );
}
